/**
 * CSV conversion for extraction results.
 *
 * The bookkeeping/accounting audience (our primary ICP) lives in spreadsheets,
 * not JSON. This utility turns a structured extraction into a CSV string that
 * imports cleanly into Excel and Google Sheets.
 *
 * Output strategy depends on the shape of the result:
 *
 *   - If the result has a `line_items` or `items` array (receipts, invoices):
 *     expand to ONE ROW PER LINE ITEM, repeating doc-level fields on each row.
 *     This is what an accountant actually wants — they can sort, sum, and
 *     filter line items across many receipts in the same sheet.
 *
 *   - If the result has a `blocks` array (pdf_to_text schema): one row per
 *     block with columns (type, level, content).
 *
 *   - Otherwise (business_card, free_text, single-line receipts): one row
 *     with all flat fields.
 *
 * Nested objects beyond one level are JSON-serialized into the cell.
 */

type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

/** RFC 4180 quoting — quote any field that contains comma, quote, or newline. */
function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  let s: string;
  if (typeof value === "string") s = value;
  else if (typeof value === "number" || typeof value === "boolean") s = String(value);
  else s = JSON.stringify(value);

  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toRow(values: unknown[]): string {
  return values.map(escapeCell).join(",");
}

/**
 * Convert an extraction result (the JSON returned by /extract) into CSV.
 * Returns null if the input is empty.
 */
export function resultToCsv(result: Record<string, unknown> | null): string | null {
  if (!result || typeof result !== "object") return null;

  // Line-item expansion path (receipt, invoice, etc.)
  const lineItemsKey = ["line_items", "items"].find(
    (k) => Array.isArray((result as Record<string, unknown>)[k]),
  );
  if (lineItemsKey) {
    return lineItemRowsCsv(result, lineItemsKey);
  }

  // pdf_to_text blocks path
  if (Array.isArray((result as Record<string, unknown>).blocks)) {
    return blocksCsv(result);
  }

  // Default: single row, all top-level fields
  return flatRowCsv(result);
}

function flatRowCsv(result: Record<string, unknown>): string {
  const entries = Object.entries(result);
  const headers = entries.map(([k]) => k);
  const values = entries.map(([, v]) => v);
  return toRow(headers) + "\r\n" + toRow(values) + "\r\n";
}

function lineItemRowsCsv(result: Record<string, unknown>, key: string): string {
  const items = (result[key] as Record<string, unknown>[]) ?? [];

  // Doc-level fields = everything except the line items array
  const docFields = Object.entries(result).filter(([k]) => k !== key);
  const docHeaders = docFields.map(([k]) => k);

  // Union of all keys across line items, preserving first-seen order
  const itemHeaderSet: string[] = [];
  for (const item of items) {
    if (item && typeof item === "object") {
      for (const k of Object.keys(item)) {
        if (!itemHeaderSet.includes(k)) itemHeaderSet.push(k);
      }
    }
  }
  const itemHeaders = itemHeaderSet.map((k) => `item.${k}`);

  const allHeaders = [...docHeaders, ...itemHeaders];

  const lines: string[] = [toRow(allHeaders)];

  if (items.length === 0) {
    // No line items — still emit one row so the doc-level fields are exported
    const docValues = docFields.map(([, v]) => v);
    lines.push(toRow([...docValues, ...itemHeaderSet.map(() => null)]));
  } else {
    for (const item of items) {
      const docValues = docFields.map(([, v]) => v);
      const itemValues = itemHeaderSet.map((k) =>
        item && typeof item === "object" ? (item as Record<string, unknown>)[k] ?? null : null,
      );
      lines.push(toRow([...docValues, ...itemValues]));
    }
  }

  return lines.join("\r\n") + "\r\n";
}

function blocksCsv(result: Record<string, unknown>): string {
  const blocks = (result.blocks as Record<string, unknown>[]) ?? [];
  const title = result.title;
  const language = result.language;
  const pageCount = result.page_count;

  const headers = ["title", "language", "page_count", "block_index", "type", "level", "content"];
  const lines: string[] = [toRow(headers)];
  blocks.forEach((b, i) => {
    lines.push(
      toRow([
        title,
        language,
        pageCount,
        i,
        b?.type ?? null,
        b?.level ?? null,
        b?.content ?? null,
      ]),
    );
  });
  return lines.join("\r\n") + "\r\n";
}

/**
 * Trigger a browser download for the given CSV string. Adds the UTF-8 BOM
 * so Excel recognizes the encoding without a manual import step.
 */
export function downloadCsv(filename: string, csv: string): void {
  // BOM lets Excel auto-detect UTF-8 (without this, accented characters break in Excel)
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export type { Json };
